import { uuid4 } from '@sentry/core'
import { differenceInCalendarDays } from 'date-fns'
import {
  GeckoAssetModel,
  GeckoInspection,
  GeckoMachinedForgingSimple,
  GeckoMillPlan,
  RolodexAuditLogObject,
} from 'types'
import { parseDate, parseDateAndSetToEST } from 'utils/parsingHelpers'

export type EventType = {
  id: string
  type: 'inspection' | 'mill-plan' | 'status'
  event_text: string
  date: Date
  person: string
  url?: string
}

const pushEvent = (
  events: Record<string, EventType[]>,
  componentId: string | undefined,
  event: EventType
) => {
  if (!componentId) return
  if (!events[componentId]) events[componentId] = []
  events[componentId].push(event)
}

export const getComponentFromManufacturedDate = <
  T extends { manufactured_date?: string },
>(
  referenceDate: Date,
  sortedComponents: readonly T[]
) => {
  type ComponentWithDate = {
    component: T
    manufacturedDate: Date
  }
  const mostRecentValidComponent = sortedComponents
    .map((component) => {
      const manufacturedDate = parseDateAndSetToEST(component.manufactured_date)
      if (!manufacturedDate) return undefined
      return {
        component,
        manufacturedDate,
      }
    })
    .filter(
      (componentWithDate): componentWithDate is ComponentWithDate =>
        !!componentWithDate &&
        differenceInCalendarDays(
          componentWithDate.manufacturedDate,
          referenceDate
        ) <= 0
    )
    .reduce<ComponentWithDate | undefined>((acc, curr) => {
      if (!acc) return curr
      return differenceInCalendarDays(
        curr.manufacturedDate,
        acc.manufacturedDate
      ) > 0
        ? curr
        : acc
    }, undefined)

  return mostRecentValidComponent?.component
}

export const sortComponentsByManufacturedDate = <
  T extends { manufactured_date?: string },
>(
  components: readonly T[],
  sortAscending: boolean
) => {
  return [...components].sort((a, b) => {
    const ta = a.manufactured_date ? new Date(a.manufactured_date).getTime() : 0
    const tb = b.manufactured_date ? new Date(b.manufactured_date).getTime() : 0
    return sortAscending ? ta - tb : tb - ta
  })
}

export const sortMachinedForgingAssetModelComponents = (
  assetModel: GeckoAssetModel,
  sortAscending: boolean
) => {
  const components = Array.from(
    assetModel.data.components.values()
  ) as readonly GeckoMachinedForgingSimple[]

  return sortComponentsByManufacturedDate(components, sortAscending)
}

export const buildEventsByComponentId = (
  sortedComponents: readonly GeckoMachinedForgingSimple[],
  inspections: readonly GeckoInspection[],
  millPlans: readonly GeckoMillPlan[],
  assetAuditLogs: RolodexAuditLogObject | undefined,
  sortAscending: boolean
): Record<string, EventType[]> => {
  const eventsByComponentId: Record<string, EventType[]> = {}

  // Inspections
  for (const insp of inspections) {
    const componentId = insp.tags.component
    pushEvent(eventsByComponentId, componentId, {
      id: insp.id,
      type: 'inspection',
      event_text: 'Inspection Occurred',
      date: insp.data.date,
      person: String((insp.data as Record<string, unknown>)['person'] || ''),
      url: `/details/${insp.id}`,
    })
  }

  // Mill plans
  for (const plan of millPlans) {
    const componentId = plan.tags.component
    pushEvent(eventsByComponentId, componentId, {
      id: plan.id,
      type: 'mill-plan',
      event_text: 'Mill Plan Published',
      date: new Date(plan.updated_at),
      person: '',
      url: `/details/${componentId}?extension=MillPlanning`,
    })
  }

  // Status audit logs
  const auditLogArray = Object.values(assetAuditLogs || {}).flat()
  const statusChangeLogs = auditLogArray
    .map((log) => ({
      log,
      oldStatus: log.old_value?.data?.status,
      newStatus: log.new_value?.data?.status,
    }))
    .filter(
      ({ oldStatus, newStatus }) =>
        oldStatus && newStatus && oldStatus !== newStatus
    )

  for (const statusChangeLog of statusChangeLogs) {
    const date = parseDate(statusChangeLog.log.timestamp)
    if (!date) continue
    const person = String(
      statusChangeLog.log.metadata.user_name ||
        statusChangeLog.log.metadata.user_email ||
        'Gecko Robotics'
    )
    const component = getComponentFromManufacturedDate(date, sortedComponents)
    pushEvent(eventsByComponentId, component?.id, {
      id: uuid4().slice(0, 8),
      type: 'status',
      event_text: `${person} changed status from ${statusChangeLog.oldStatus} to ${statusChangeLog.newStatus}`,
      date,
      person,
    })
  }

  // Sort per-component events
  for (const key of Object.keys(eventsByComponentId)) {
    eventsByComponentId[key].sort((a, b) => {
      const ta = a.date instanceof Date ? a.date.getTime() : 0
      const tb = b.date instanceof Date ? b.date.getTime() : 0
      return sortAscending ? ta - tb : tb - ta
    })
  }

  return eventsByComponentId
}
